import axios from 'axios';
import { normalize } from 'normalizr';
import { fromBusinessMemberDto } from '../../mappers/business/toBusinessMemberModel';
import {
    fromBusinessDto,
    fromBusinessLiteDto,
    Role,
    toBusinessStatsModel,
} from '../../mappers/business/toBusinessModel';
import { toBusinessPersistenceDto } from '../../mappers/business/toBusinessPersistenceDto';
import {
    toBloggerRegistrationPersistenceDto,
    toBusinessRegistrationPersistenceDto,
} from '../../mappers/business/toRegistrationPersistenceDto';
import { fromCategoriesTreeDto } from '../../mappers/categories/fromCategoriesTreeDto';
import { share } from '../../utils/share';
import { ReportReasons } from '../posts/types';
import { UserModel } from '../user/types';
import { BusinessCollection } from './constants';
import { businessSchema } from './schema';
import {
    BusinessStatisticParams,
    BusinessType,
    type BloggerRegistrationModel,
    type BusinessDataModel,
    type BusinessMemberModel,
    type BusinessModel,
    type BusinessPersistenceModel,
    type BusinessRegistrationModel,
    type CategoryModel,
} from './types';
import type { EventSource } from '../../utils/analyticsService';

export async function loadBusinessById(businessId: string): Promise<BusinessModel> {
    const response = await axios.get(`/business/${businessId}`);

    return fromBusinessDto(response.data);
}

export async function loadBusinessStatistic(
    businessId: string,
    period?: string | null,
    dateFrom?: string | null,
    dateTo?: string | null,
) {
    let url = `/business/${businessId}/stat?${period ? `period=${period}` : ''}`;

    if (dateFrom) {
        url += `&date_from=${dateFrom}`;
    }

    if (dateTo) {
        url += `&date_to=${dateTo}`;
    }

    const response = await axios.get(url);

    return {
        stats: toBusinessStatsModel(response.data.stats),
    };
}

export async function loadBusinessCollection(
    collection: BusinessCollection,
    params: Record<string, any>,
): Promise<{
    business: Array<BusinessDataModel['id']>;
    records: {
        business: BusinessDataModel[];
    };
}> {
    const response = await axios.get(
        collection === BusinessCollection.SUBSCRIPTIONS ? '/user/subscriptions' : '/business',
        { params },
    );
    const businesses = response.data.businesses || [];
    const normalized = normalize(
        { business: businesses.map(fromBusinessLiteDto) },
        { business: [businessSchema] },
    );

    return {
        business: normalized.result.business,
        records: {
            business: Object.values<BusinessDataModel>(normalized.entities.business ?? {}),
        },
    };
}

export async function updateBusiness(
    businessId: string,
    model: BusinessPersistenceModel,
): Promise<{
    business: Array<BusinessDataModel['id']>;
    records: {
        business: BusinessDataModel[];
    };
}> {
    const response = await axios(`/business/${businessId}`, {
        method: 'PATCH',
        data: toBusinessPersistenceDto(model),
    });

    const normalized = normalize(
        { business: fromBusinessDto(response.data) },
        { business: businessSchema },
    );

    return {
        business: normalized.result.business,
        records: {
            business: Object.values<BusinessDataModel>(normalized.entities.business ?? {}),
        },
    };
}

export async function removeBusiness(
    business?: BusinessModel,
    currentUser?: UserModel,
): Promise<void> {
    if (!business) {
        throw new Error('Не удалось удалить бизнес');
    }
    if (business.id === currentUser?.profile?.id && currentUser.role === Role.SUPER_ADMIN) {
        throw new Error('Нельзя удалить общий бизнес');
    }
    await axios.delete(`/business/${business.id}`);
}

export async function subscribeToBusiness(
    model: BusinessModel,
    params: {
        eventSource: EventSource;
        postNumber?: number;
        postId?: string;
        collectionId?: string;
    },
): Promise<void> {
    await axios(`/business/${model.id}/subscribe`, {
        method: model.subscribed ? 'DELETE' : 'PUT',
        params: {
            post_number: params.postNumber,
            event_source: params.eventSource,
            post_id: params.postId,
            collection_id: params.collectionId,
        },
    });
}

export async function shareBusiness(model: BusinessModel): Promise<ReturnType<typeof share>> {
    return await share({ url: model.shareUrl });
}

export async function searchBusinesses(query: string): Promise<BusinessModel[]> {
    const response = await axios.get('/business/', { params: { query, amount: 10 } });

    return response.data.businesses.map(fromBusinessLiteDto);
}

export async function getBusinessMembers(businessId: string): Promise<BusinessMemberModel[]> {
    const response = await axios.get(`/business/${businessId}/members`);

    return response.data.members.map(fromBusinessMemberDto);
}

export async function generateInviteLink(businessId: string): Promise<string> {
    const response = await axios.post(`/business/${businessId}/invite`);

    return response.data.invite_url;
}

export async function approveMember(businessId: string, memberId: string): Promise<boolean> {
    return axios.put(`/business/${businessId}/members/${memberId}/approve`);
}

export async function removeMember(businessId: string, memberId: string): Promise<boolean> {
    return axios.delete(`/business/${businessId}/members/${memberId}`);
}

export async function registerBusiness(
    model: BusinessRegistrationModel,
): Promise<string | undefined> {
    const data =
        model.type === BusinessType.BLOGGER
            ? toBloggerRegistrationPersistenceDto(model)
            : toBusinessRegistrationPersistenceDto(model);

    const result = await axios.post('/business/registration', data);

    return result?.data?.tg_verification_id;
}

export async function registerBlogger(
    model: BloggerRegistrationModel,
): Promise<string | undefined> {
    const result = await axios.post(
        '/blogger/registration',
        toBloggerRegistrationPersistenceDto(model),
    );

    return result?.data?.tg_verification_id;
}

export async function reportBusiness(businessId: string, reason: ReportReasons): Promise<void> {
    await axios.put(`/business/${businessId}/report`, {}, { params: { report_reason: reason } });
}

export async function loadCategoriesTree(): Promise<CategoryModel[]> {
    const response = await axios.get('/categories');

    return fromCategoriesTreeDto(response.data.categories);
}

export async function getXlsxStatistic(
    businessId: string,
    { statuses, data_sources, period, date_from, date_to, stat_columns }: BusinessStatisticParams,
): Promise<{ blob: Blob; filename: string }> {
    const result = await axios.get(`/business/${businessId}/posts_stat/export`, {
        params: {
            period,
            date_from,
            date_to,
            statuses: statuses && statuses.length > 0 ? JSON.stringify(statuses) : undefined,
            data_sources:
                data_sources && data_sources.length > 0 ? JSON.stringify(data_sources) : undefined,
            stat_columns: JSON.stringify(stat_columns),
        },
        responseType: 'blob',
    });

    if (!result?.data) {
        throw new Error('Не удалось загрузить статистику');
    }

    const blob = new Blob([result.data], {
        type: 'application/xlsx',
    });

    return { blob, filename: `posts_stat_${businessId}` };
}
